import { useEffect, useRef, useState } from 'react';

/* ── Price slider: a native range input — smooth with mouse, touch and keyboard ──
 * It moves along a logarithmic scale (0–1000 positions), so the many low-priced products are not squeezed into the
 * first few millimetres when one product costs much more than the rest. */
const SLIDER_STEPS = 1000;
const niceRound = (v) => (v < 2000 ? Math.round(v / 10) * 10 : v < 10000 ? Math.round(v / 50) * 50 : Math.round(v / 500) * 500);
const posToPrice = (pos, floor, ceiling) =>
  (pos >= SLIDER_STEPS ? ceiling : niceRound(floor * (ceiling / floor) ** (pos / SLIDER_STEPS)));
const priceToPos = (price, floor, ceiling) =>
  (price >= ceiling ? SLIDER_STEPS : Math.max(0, Math.round((SLIDER_STEPS * Math.log(Math.max(price, floor) / floor)) / Math.log(ceiling / floor))));

/** Slider range for a list of prices: from the cheapest to the dearest product, rounded outwards */
export const priceBounds = (prices) => {
  const positive = prices.filter((v) => v > 0);
  if (!positive.length) return { floor: 100, ceiling: 1000 };
  const top = Math.max(...positive);
  const low = Math.min(...positive);
  const ceiling = top <= 1000 ? Math.ceil(top / 50) * 50 : Math.ceil(top / 100) * 100;
  return { floor: Math.max(10, Math.min(Math.floor(low / 10) * 10, ceiling - 10)), ceiling };
};

/**
 * Holds its own thumb position, so dragging only redraws this small control. The (heavier) product grid is told
 * the new limit when the shopper pauses for a moment or lets go. `limit` null = any price.
 */
const PriceFilter = ({ floor, ceiling, limit, onCommit, symbol = '₹' }) => {
  const scaleOk = ceiling > floor;
  const toPos = (price) => (price == null || !scaleOk ? SLIDER_STEPS : priceToPos(price, floor, ceiling));
  const [pos, setPos] = useState(() => toPos(limit));
  const timer = useRef(null);
  const lastSent = useRef(limit);

  // follow outside changes (quick chips, "clear all", a new catalogue) but not our own commits
  useEffect(() => {
    if (limit !== lastSent.current) { lastSent.current = limit; setPos(toPos(limit)); }
  }, [limit, floor, ceiling]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => clearTimeout(timer.current), []);

  const price = scaleOk ? posToPrice(pos, floor, ceiling) : ceiling;
  const commit = (p) => {
    clearTimeout(timer.current);
    const value = p >= SLIDER_STEPS || !scaleOk ? null : posToPrice(p, floor, ceiling);
    if (value === lastSent.current) return;
    lastSent.current = value;
    onCommit(value);
  };
  const fmt = (v) => `${symbol}${v.toLocaleString('en-IN')}`;

  return (
    <div>
      <div className="flex justify-between items-center text-xs">
        <span className="text-ink-soft">Up to</span>
        <span className="font-bold text-primary bg-primary-light px-2.5 py-0.5 rounded-full tabular-nums">
          {pos >= SLIDER_STEPS ? 'Any price' : fmt(price)}
        </span>
      </div>
      <input
        type="range" min={0} max={SLIDER_STEPS} step={1} value={pos}
        onChange={(e) => {
          const p = Number(e.target.value);
          setPos(p);
          clearTimeout(timer.current);
          timer.current = setTimeout(() => commit(p), 150);
        }}
        onPointerUp={(e) => commit(Number(e.currentTarget.value))}
        onKeyUp={(e) => commit(Number(e.currentTarget.value))}
        aria-label="Maximum price"
        aria-valuetext={pos >= SLIDER_STEPS ? 'Any price' : `Up to ${fmt(price)}`}
        className="price-range w-full my-3"
        style={{ '--pct': `${(pos / SLIDER_STEPS) * 100}%` }}
      />
      <div className="flex justify-between text-[10px] text-ink-muted -mt-1">
        <span>{fmt(floor)}</span><span>{fmt(ceiling)}</span>
      </div>
    </div>
  );
};

export default PriceFilter;
